/**
 * AuditDetail, sheet 44 callouts 5 to 8 and 10 to 12 (mission 10 WP17 T101; FR-018, FR-023 items 1 and 5; A.5,
 * TX.4, TX.6, D.1, D.6, T.1, TX.3; spec amendment 20; OR-15, U18; contracts/audit-api.md "What the writers
 * record"; research §1.27 "Sheet 44").
 *
 * One renderer per written type, reading only the fields its writer records: a field that is absent or empty
 * renders nothing, and nothing is invented. Each row is marked `data-audit-field` with the fields it shows.
 * - transmission: bureau, bytes, result and run; no remote filename is recorded (44/5's deviation);
 * - download: which file (`kind`), the run and the sha256 in full mono; no size is recorded (44/6); an
 *   `audit_export` shows its recorded from, to and type count, never the free text;
 * - error_acknowledgment: the count as plain text, never a severity badge, so §11's ANA.4-legend rule is not
 *   triggered here (U26); no rule list is recorded (44/7);
 * - retention_changed: old → new (44/8); export and deletion requests and completions with their request,
 *   due date, counts and the key's destroy date (44/10); terms acceptance with its version (44/10);
 * - credential and header-parameter changes: the field names only, never an identifier value or last 4 (44/11);
 * - sign-ins: the method only; no device is recorded (44/12); a failed one its reason;
 * - the eleven billing types, each by every field its writer records; a plan by register 36's plan label.
 * Ids are short in mono with the full id as the `title`, counts mono, dates UTC in `<time datetime>`.
 *
 * `Recorded` is the IP and user-agent cell: the value as recorded, blank when the event recorded none (FR-023
 * item 1; US8 scenario 5), never "unknown".
 */
import type { ReactNode } from 'react'

import { BUREAUS, bureauName, type Bureau } from '@/lib/api/bureaus'
import type { AuditEvent, AuditEventType } from '@/lib/api/audit'
import { TRANSMISSION_RESULTS, type TransmissionResult } from '@/lib/api/transmissions'
import { Mono, count, date, isoDateTime, shortId, time } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'

type Detail = Readonly<Record<string, string>>

interface Row {
  fields: readonly string[]
  /** A register 37 `auditDetail.*` label; a sentence row has none. */
  label?: string
  value: ReactNode
}

const RESULT_LABEL: Record<TransmissionResult, TextKey> = {
  pending: 'resultLabel.pending',
  success: 'resultLabel.success',
  auth_failed: 'resultLabel.authFailed',
  timeout: 'resultLabel.timeout',
  not_sent: 'resultLabel.notSent',
}

function has(detail: Detail, key: string): boolean {
  return typeof detail[key] === 'string' && detail[key] !== ''
}

const NUMBER = /^\d+$/

function bureau(value: string): string {
  return (BUREAUS as readonly string[]).includes(value) ? bureauName(value as Bureau) : value
}

/** Register 36's plan labels (cycle 2, review finding 5); any other code shows as recorded. */
const PLAN_LABEL: Readonly<Record<string, TextKey>> = { monthly: 'planLabelMonthly', annual: 'planLabelAnnual' }

function plan(value: string): string {
  const key = PLAN_LABEL[value]
  return key === undefined ? value : copyText(key)
}

function result(value: string): string {
  return (TRANSMISSION_RESULTS as readonly string[]).includes(value) ? copyText(RESULT_LABEL[value as TransmissionResult]) : value
}

/** How a recorded value renders. */
const FORMAT = {
  text: (v: string): ReactNode => v,
  count: (v: string): ReactNode => <Mono>{NUMBER.test(v) ? count(Number(v)) : v}</Mono>,
  id: (v: string): ReactNode => <Mono title={v}>{shortId(v)}</Mono>,
  hash: (v: string): ReactNode => <Mono className="break-all">{v}</Mono>,
  day: (v: string): ReactNode => (
    <time dateTime={v} className="font-mono">
      {date(v)}
    </time>
  ),
  instant: (v: string): ReactNode => (
    <time dateTime={isoDateTime(v)} className="font-mono">
      {date(v)} {time(v)}
    </time>
  ),
  bureau: (v: string): ReactNode => v.split(',').map(bureau).join(', '),
  result: (v: string): ReactNode => result(v),
  plan: (v: string): ReactNode => plan(v),
  names: (v: string): ReactNode => <Mono>{v.split(',').join(', ')}</Mono>,
}

type Format = keyof typeof FORMAT

/** One labelled row per recorded field: `[field, label, format]`. */
type FieldSpec = readonly [field: string, label: TextKey, format: Format]

function rows(detail: Detail, specs: readonly FieldSpec[]): Row[] {
  return specs
    .filter(([field]) => has(detail, field))
    .map(([field, label, format]) => ({ fields: [field], label: copyText(label), value: FORMAT[format](detail[field] as string) }))
}

const ACTOR: FieldSpec = ['actor', 'auditDetail.actor', 'text']
const PLAN: FieldSpec = ['plan', 'auditDetail.plan', 'plan']
const REQUEST: FieldSpec = ['request_id', 'auditDetail.request', 'id']
const DUE_ON: FieldSpec = ['due_on', 'auditDetail.dueOn', 'day']
const RUN: FieldSpec = ['run_id', 'auditDetail.run', 'id']
const BUREAU: FieldSpec = ['bureau', 'auditDetail.bureau', 'bureau']
const RESULT: FieldSpec = ['result', 'auditDetail.result', 'result']
const REASON: FieldSpec = ['reason', 'auditDetail.reason', 'text']

const of =
  (...specs: FieldSpec[]) =>
  (detail: Detail): Row[] =>
    rows(detail, specs)

const none = (): Row[] => []

function download(detail: Detail): Row[] {
  if (detail['kind'] === 'audit_export') {
    return rows(detail, [
      ['kind', 'auditDetail.file', 'text'],
      ['from', 'auditDetail.from', 'day'],
      ['to', 'auditDetail.to', 'day'],
      ['types', 'auditDetail.typeCount', 'count'],
    ])
  }
  return rows(detail, [['kind', 'auditDetail.file', 'text'], RUN, ['sha256', 'auditDetail.checksum', 'hash']])
}

function errorAcknowledgment(detail: Detail): Row[] {
  const out: Row[] = []
  if (has(detail, 'error_count')) {
    const n = detail['error_count'] as string
    out.push({ fields: ['error_count'], value: PROVISIONAL_COPY['auditDetail.errorCount'].render({ count: NUMBER.test(n) ? Number(n) : n }) })
  }
  return [...out, ...rows(detail, [['bureaus', 'auditDetail.bureau', 'bureau'], ['ack_wording_version', 'auditDetail.version', 'text'], RUN])]
}

function retentionChanged(detail: Detail): Row[] {
  const from = detail['from']
  const to = detail['to']
  if (from === undefined || to === undefined || !NUMBER.test(from) || !NUMBER.test(to)) return []
  return [{ fields: ['from', 'to'], value: PROVISIONAL_COPY['auditDetail.retention'].render({ from: Number(from), to: Number(to) }) }]
}

/** `{<field>: "changed"}`: the names alone (44/11). */
function headerParamChanged(detail: Detail): Row[] {
  const names = Object.keys(detail).filter((k) => detail[k] === 'changed')
  if (names.length === 0) return []
  return [{ fields: names, label: copyText('auditDetail.fieldsChanged'), value: <Mono>{names.join(', ')}</Mono> }]
}

const RENDERERS: Record<AuditEventType, (detail: Detail) => Row[]> = {
  login: of(['method', 'auditDetail.method', 'text']),
  login_failed: of(REASON),
  logout: none,
  download,
  transmission: of(BUREAU, ['bytes', 'auditDetail.bytes', 'count'], RESULT, RUN),
  error_acknowledgment: errorAcknowledgment,
  credential_changed: of(BUREAU, ['fields', 'auditDetail.fieldsChanged', 'names']),
  credential_test_started: of(BUREAU),
  credential_tested: of(BUREAU, RESULT),
  header_param_changed: headerParamChanged,
  terms_accepted: of(['version', 'auditDetail.version', 'text']),
  tenant_verified: of(ACTOR),
  mfa_enrolled: none,
  backup_codes_regenerated: none,
  password_reset: none,
  billing_checkout_started: of(PLAN, ACTOR),
  billing_subscribed: of(PLAN),
  billing_cancel_scheduled: of(ACTOR),
  billing_cancel_undone: of(ACTOR),
  billing_plan_switch_scheduled: of(PLAN, ACTOR),
  billing_card_update_started: of(ACTOR),
  credits_purchased: of(['credits', 'auditDetail.credits', 'count'], ACTOR),
  billing_dunning_started: of(['failed_at', 'auditDetail.failedAt', 'instant']),
  billing_read_only_started: of(REASON),
  billing_read_only_ended: of(['to', 'auditDetail.to', 'text']),
  billing_email_changed: of(['from', 'auditDetail.from', 'text'], ['to', 'auditDetail.to', 'text'], ACTOR),
  retention_changed: retentionChanged,
  data_export_request: of(REQUEST, DUE_ON, ['reopened_from', 'auditDetail.reopenedFrom', 'id']),
  deletion_request: of(REQUEST, DUE_ON, ['queued_runs_cancelled', 'auditDetail.queuedRunsCancelled', 'count']),
  data_export_completed: of(REQUEST, ['files', 'auditDetail.files', 'count'], ['bytes', 'auditDetail.bytes', 'count']),
  deletion_completed: of(REQUEST, ['key_destroy_on', 'auditDetail.keyDestroyOn', 'day']),
}

export function AuditDetail({ event }: { event: AuditEvent }) {
  const render = (RENDERERS as Partial<Record<string, (detail: Detail) => Row[]>>)[event.type]
  if (render === undefined) return null
  return (
    <div data-audit-detail={event.type} className="flex flex-col gap-0.5 py-1.5 text-body-sm text-navy-950">
      {render(event.detail).map((row) => (
        <p key={row.fields.join(' ')} data-audit-field={row.fields.join(' ')}>
          {row.label === undefined ? null : (
            <>
              <span data-audit-label="" className="text-navy-600">
                {row.label}
              </span>{' '}
            </>
          )}
          {row.value}
        </p>
      ))}
    </div>
  )
}

/** An IP or user-agent cell: the recorded value, or blank (FR-023 item 1). */
export function Recorded({ value, mono = false }: { value: string | null; mono?: boolean }) {
  if (value === null || value === '') return null
  return mono ? (
    <Mono title={value} className="block truncate text-body-sm">
      {value}
    </Mono>
  ) : (
    <span title={value} className="block truncate text-body-sm">
      {value}
    </span>
  )
}
